---
title: "Printing borrower cards"
slug: "printing-borrower-cards"
category: "operations"
description: "Turn a school printer into a borrower card factory — no label printer required."
tags: ["cards", "print", "borrowers", "labels", "barcodes"]
order: 3
---

BookBurrow can print borrower cards on whatever printer your school already has. You don't need a Dymo, you don't need a label printer, and you don't need to laminate (though many schools do).

## What a borrower card looks like

A BookBurrow borrower card is a small rectangle with:

- The pupil's name
- Their year group and class
- A barcode of their card number
- Your school name (optional) and logo (optional)

You can choose from four templates: **Minimal**, **Friendly** (with a burrow illustration), **Formal** (for secondary), and **Sticker** (small, for sticking inside a reading record).

## Printing cards for a whole year group

The most common case: September, new intake, you need ninety Year 7 cards. This assumes you've already [imported the new cohort from your MIS](/help/guides/importing-from-sims-arbor-bromcom) or [added each pupil by hand](/help/guides/adding-borrowers-manually) — you can't print cards for pupils who don't exist yet.

<Steps>
  <Step title="Open Borrowers tab">
    Filter to the year group you want.
  </Step>
  <Step title="Click Select all">
    The checkbox in the column header selects every filtered borrower.
  </Step>
  <Step title="Click Bulk actions → Print cards">
    Pick a template (Minimal is the default) and a paper layout.
  </Step>
  <Step title="Preview the print layout">
    BookBurrow generates a PDF that lays the cards out on standard A4 — usually 8 or 10 per page depending on the template.
  </Step>
  <Step title="Print">
    Use your normal school printer. Plain paper is fine; many schools use a slightly heavier card (160 gsm) for durability.
  </Step>
</Steps>

## Reprinting for one pupil

Kids lose cards. To reprint just one:

<Steps>
  <Step title="Open that borrower's record">
    Click their row in the Borrowers tab.
  </Step>
  <Step title="Click Print card">
    Same layout options as bulk print, but only one card.
  </Step>
</Steps>

<Callout variant="tip" title="Change card number or keep it?">
Before reprinting, decide whether you want to give the pupil a new card number (so the lost card can't be used by someone else) or keep the old one. In a primary school you usually keep it; in a secondary you usually change it. BookBurrow asks you either way.
</Callout>

## Laminating and durability

Plain paper cards last about a term before they look sad. Three practical upgrades:

- **Heavier card**: 160 gsm or 200 gsm card runs through most school laser printers and gives you a much more durable card.
- **Lamination**: small pouches are cheap and give cards a multi-year life. Check your laminator can handle the card thickness.
- **Sticker template**: print the card as a sticker and apply it to an existing school ID badge.

## Sticker labels for inside books

The **Sticker** template is also used for book spine labels and book pockets. It's sized for standard 65-labels-per-sheet Avery-style L7651 sticker sheets, which are available from any stationery supplier. You can generate a run of item barcode stickers with matching title lines for the whole of a new book order.

## Logos and school branding

In **Settings → Branding** you can upload your school logo (any common image format) and set a school colour. Both show up on the Friendly and Formal templates. This is a five-minute job that makes the cards feel properly official.
